import { BlockBlobClient } from "@azure/storage-blob";
import type { UploadFile } from "antd/es/upload/interface";
import { axiosBase, errorHandler } from "./axios";
import { getAccessToken } from "./helper";
import magickWasmUrl from "@imagemagick/magick-wasm/magick.wasm?url";
import { io, type Socket } from "socket.io-client";
import {
    buildAlbumUploadResumeId,
    deleteAlbumUploadResumeRecord,
    getAlbumUploadResumeRecord,
    purgeStaleAlbumUploadResumeRecords,
    saveAlbumUploadResumeRecord,
    type AlbumUploadResumeRecord,
} from "./albumUploadResumeStore";

const ALBUM_SAS_UPLOAD_ENDPOINT = "/api/v1/album-selection-media/upload";
const ALBUM_LIST_ENDPOINT = "/api/v1/album-selection-media/list";
const ALBUM_UPLOAD_STATUS_ENDPOINT = "/api/v1/album-selection-media/upload-status";
const BLOCK_SIZE = 4 * 1024 * 1024;
const BLOCK_CONCURRENCY = 4;
const FILE_CONCURRENCY = 5;
const STALE_RESUME_RECORD_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const SOCKET_HEARTBEAT_INTERVAL_MS = 10000;

export interface AlbumListedMediaItem {
    blobName: string;
    name: string;
    url?: string;
    thumbUrl?: string;
    size: number;
}

export interface AlbumUploadProgress {
    fileName: string;
    loadedBytes: number;
    totalBytes: number;
    percentage: number;
}

export interface AlbumUploadSuccessItem {
    fileName: string;
}

export interface AlbumUploadFailedItem {
    fileName: string;
    error: string;
}

export interface AlbumUploadResult {
    success: AlbumUploadSuccessItem[];
    failed: AlbumUploadFailedItem[];
}

interface AlbumUploadOptions {
    onProgress?: (progress: AlbumUploadProgress) => void;
}

type BrowserUploadInput = File | UploadFile;

const getSafeLastModified = (file: File) => Number.isFinite(file.lastModified) ? file.lastModified : 0;
const getTotalBlockCount = (fileSize: number, blockSize: number) => fileSize <= 0 ? 0 : Math.ceil(fileSize / blockSize);
const getBlockBounds = (blockIndex: number, blockSize: number, fileSize: number) => {
    const start = blockIndex * blockSize;
    const end = Math.min(fileSize, start + blockSize);
    return { start, end, length: Math.max(0, end - start) };
};
const getBlockId = (blockIndex: number) => btoa(`block-${String(blockIndex).padStart(8, "0")}`);
const normalizeCompleted = (completed: number[], totalBlocks: number) =>
    Array.from(new Set((completed || []).filter((i) => Number.isInteger(i) && i >= 0 && i < totalBlocks))).sort((a, b) => a - b);
const getUploadedBytes = (completed: number[], fileSize: number, blockSize: number) =>
    completed.reduce((total, i) => total + getBlockBounds(i, blockSize, fileSize).length, 0);

const getRawFile = (file: BrowserUploadInput): File => {
    if (file instanceof File) return file;
    if (file.originFileObj instanceof File) return file.originFileObj;
    throw new Error(`Missing file data for ${file.name}`);
};

const getErrorMessage = (error: unknown) => (error instanceof Error ? error.message : "Upload failed");

const joinBlobPath = (...parts: string[]) => parts.map((p) => p.replace(/^\/+|\/+$/g, "")).join("/");

const normalizeUploadUrl = (uploadUrl: string) => uploadUrl.split("?")[0].replace(/\/+$/, "");

const createUploadSocket = (): Socket | null => {
    const token = getAccessToken();
    const socketUrl = typeof axiosBase.defaults.baseURL === "string" && axiosBase.defaults.baseURL
        ? axiosBase.defaults.baseURL
        : window.location.origin;
    if (!token) return null;
    try {
        return io(socketUrl, { transports: ["websocket"], auth: { token }, reconnection: true });
    } catch {
        return null;
    }
};

const createResumeRecord = ({
    studio,
    eventId,
    serviceId,
    templateId,
    file,
    blockSize,
    blobName,
    totalBlocks,
}: {
    studio: string;
    eventId: string;
    serviceId?: string | null;
    templateId: string;
    file: File;
    blockSize: number;
    blobName: string;
    totalBlocks: number;
}): AlbumUploadResumeRecord => {
    const lastModified = getSafeLastModified(file);
    return {
        id: buildAlbumUploadResumeId({
            studioId: studio,
            eventId,
            serviceId,
            templateId,
            fileName: file.name,
            fileSize: file.size,
            lastModified,
        }),
        studioId: studio,
        eventId,
        serviceId,
        templateId,
        fileName: file.name,
        fileSize: file.size,
        lastModified,
        blobName,
        blockSize,
        totalBlocks,
        completedBlockIndexes: [],
        updatedAt: Date.now(),
    };
};

const isCompatibleResumeRecord = (
    record: AlbumUploadResumeRecord | null,
    expected: Pick<AlbumUploadResumeRecord, "blobName" | "blockSize" | "totalBlocks" | "fileSize" | "lastModified">,
) => Boolean(
    record
    && record.blobName === expected.blobName
    && record.blockSize === expected.blockSize
    && record.totalBlocks === expected.totalBlocks
    && record.fileSize === expected.fileSize
    && record.lastModified === expected.lastModified
);

const generateCanvasThumbnail = async (file: File, maxDimension = 256): Promise<Blob | null> => {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            const ratio = Math.min(maxDimension / img.width, maxDimension / img.height, 1);
            const canvas = document.createElement("canvas");
            canvas.width = Math.round(img.width * ratio);
            canvas.height = Math.round(img.height * ratio);
            const ctx = canvas.getContext("2d");
            if (!ctx) { resolve(null); return; }
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => resolve(blob), "image/webp", 0.8);
        };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
        img.src = url;
    });
};

let _magickInitPromise: Promise<void> | null = null;
const ensureMagickInitialised = (): Promise<void> => {
    if (_magickInitPromise) return _magickInitPromise;
    _magickInitPromise = import("@imagemagick/magick-wasm")
        .then(({ initializeImageMagick }) => initializeImageMagick(new URL(magickWasmUrl, location.href)))
        .catch((err) => {
            _magickInitPromise = null; 
            throw err;
        });
    return _magickInitPromise;
};

const generateRawThumbnail = async (file: File): Promise<Blob | null> => {
    try {
        await ensureMagickInitialised();
        const { ImageMagick, MagickGeometry, MagickFormat } = await import("@imagemagick/magick-wasm");
        const arrayBuffer = await file.arrayBuffer();
        const inputBytes = new Uint8Array(arrayBuffer);
        return new Promise((resolve) => {
            ImageMagick.read(inputBytes, (image) => {
                image.resize(new MagickGeometry(256, 256));
                image.write(MagickFormat.WebP, (outputBytes) => {
                    resolve(new Blob([outputBytes.buffer as ArrayBuffer], { type: "image/webp" }));
                });
            });
        });
    } catch {
        return null;
    }
};

const RAW_EXTENSIONS = new Set([
    "cr2", "cr3", "nef", "nrw", "arw", "srf", "sr2", "orf", "rw2",
    "pef", "ptx", "dng", "raw", "rwl", "raf", "x3f", "iiq",
]);

const isRawImage = (file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    return RAW_EXTENSIONS.has(ext);
};

const uploadSingleAlbumImage = async (
    file: BrowserUploadInput,
    sasPayload: { uploadUrl: string; sasToken: string; sessionId?: string },
    options?: AlbumUploadOptions,
    context?: { studio: string; eventId: string; serviceId?: string | null; templateId: string },
): Promise<AlbumUploadSuccessItem> => {
    const rawFile = getRawFile(file);
    const cleanUploadUrl = normalizeUploadUrl(sasPayload.uploadUrl);

    const blobName = rawFile.name;
    const blobUrl = joinBlobPath(cleanUploadUrl, "original", encodeURIComponent(blobName));
    const blockBlobClient = new BlockBlobClient(`${blobUrl}?${sasPayload.sasToken}`);
    const totalBlocks = getTotalBlockCount(rawFile.size, BLOCK_SIZE);

    const baseResumeRecord = context
        ? createResumeRecord({
            studio: context.studio,
            eventId: context.eventId,
            serviceId: context.serviceId,
            templateId: context.templateId,
            file: rawFile,
            blockSize: BLOCK_SIZE,
            blobName,
            totalBlocks,
        })
        : null;

    // Generate thumbnail
    let thumbnailBlob: Blob | null = null;
    try {
        if (isRawImage(rawFile)) {
            thumbnailBlob = await generateRawThumbnail(rawFile);
        } else if (rawFile.type.startsWith("image/")) {
            thumbnailBlob = await generateCanvasThumbnail(rawFile);
        }
    } catch {
        thumbnailBlob = null;
    }

    // Resume support
    let resumeRecord: AlbumUploadResumeRecord | null = baseResumeRecord;
    let completedBlockIndexes: number[] = [];

    if (baseResumeRecord) {
        const existingRecord = await getAlbumUploadResumeRecord(baseResumeRecord.id);
        if (isCompatibleResumeRecord(existingRecord, { blobName, blockSize: BLOCK_SIZE, totalBlocks, fileSize: rawFile.size, lastModified: getSafeLastModified(rawFile) })) {
            completedBlockIndexes = normalizeCompleted(existingRecord!.completedBlockIndexes, totalBlocks);
            resumeRecord = existingRecord;
        }
    }

    const remainingBlocks = Array.from({ length: totalBlocks }, (_, i) => i).filter((i) => !completedBlockIndexes.includes(i));
    const alreadyUploadedBytes = getUploadedBytes(completedBlockIndexes, rawFile.size, BLOCK_SIZE);

    options?.onProgress?.({ fileName: rawFile.name, loadedBytes: alreadyUploadedBytes, totalBytes: rawFile.size, percentage: rawFile.size > 0 ? Math.round((alreadyUploadedBytes / rawFile.size) * 100) : 0 });

    // Upload blocks in parallel batches
    for (let i = 0; i < remainingBlocks.length; i += BLOCK_CONCURRENCY) {
        const batch = remainingBlocks.slice(i, i + BLOCK_CONCURRENCY);
        await Promise.all(batch.map(async (blockIndex) => {
            const { start, end } = getBlockBounds(blockIndex, BLOCK_SIZE, rawFile.size);
            const blockData = rawFile.slice(start, end);
            const blockId = getBlockId(blockIndex);
            await blockBlobClient.stageBlock(blockId, blockData, end - start);
            completedBlockIndexes = normalizeCompleted([...completedBlockIndexes, blockIndex], totalBlocks);

            if (resumeRecord) {
                resumeRecord = { ...resumeRecord, completedBlockIndexes, updatedAt: Date.now() };
                await saveAlbumUploadResumeRecord(resumeRecord);
            }

            const loadedBytes = getUploadedBytes(completedBlockIndexes, rawFile.size, BLOCK_SIZE);
            options?.onProgress?.({ fileName: rawFile.name, loadedBytes, totalBytes: rawFile.size, percentage: rawFile.size > 0 ? Math.round((loadedBytes / rawFile.size) * 100) : 100 });
        }));
    }

    const allBlockIds = Array.from({ length: totalBlocks }, (_, i) => getBlockId(i));
    await blockBlobClient.commitBlockList(allBlockIds, { blobHTTPHeaders: { blobContentType: rawFile.type || "application/octet-stream" } });

    // Upload thumbnail
    if (thumbnailBlob) {
        try {
            const thumbBlobUrl = joinBlobPath(cleanUploadUrl, "thumbnail", encodeURIComponent(blobName) + ".webp");
            const thumbBlobClient = new BlockBlobClient(`${thumbBlobUrl}?${sasPayload.sasToken}`);
            const thumbBytes = await thumbnailBlob.arrayBuffer();
            await thumbBlobClient.uploadData(thumbBytes, { blobHTTPHeaders: { blobContentType: "image/webp" } });
        } catch {
            // non-fatal
        }
    }

    if (resumeRecord) {
        await deleteAlbumUploadResumeRecord(resumeRecord.id);
    }

    options?.onProgress?.({ fileName: rawFile.name, loadedBytes: rawFile.size, totalBytes: rawFile.size, percentage: 100 });

    return { fileName: rawFile.name };
};

export const uploadAlbumImages = async (
    files: BrowserUploadInput[],
    studio: string,
    eventId: string,
    serviceId: string | null = null,
    templateId: string,
    itemIndex: number | null = null,
    options?: AlbumUploadOptions,
): Promise<AlbumUploadResult> => {
    const success: AlbumUploadSuccessItem[] = [];
    const failed: AlbumUploadFailedItem[] = [];

    if (!files.length) return { success, failed };

    void purgeStaleAlbumUploadResumeRecords(STALE_RESUME_RECORD_TTL_MS);

    const response = await axiosBase
        .post(ALBUM_SAS_UPLOAD_ENDPOINT, { studioId: studio, eventId, serviceId, templateId, itemIndex, totalFiles: files.length })
        .then((r) => r.data)
        .catch(errorHandler) as { success?: boolean; message?: string; errorMessage?: string; resultObject?: { uploadUrl: string; sasToken: string; sessionId?: string } };

    const sasPayload = response?.resultObject;
    if (!response?.success || !sasPayload?.uploadUrl || !sasPayload?.sasToken) {
        throw new Error(response?.message || response?.errorMessage || "Unable to generate album upload link");
    }

    const socket = sasPayload.sessionId ? createUploadSocket() : null;
    let uploadedCount = 0;
    let failedCount = 0;

    const emitTracking = (eventName: "album-upload:join" | "album-upload:heartbeat" | "album-upload:complete", waitForAck = false): Promise<void> => {
        if (!socket || !sasPayload.sessionId) return Promise.resolve();
        const payload = { sessionId: sasPayload.sessionId, studioId: studio, eventId, totalFiles: files.length, uploadedCount, failedCount };
        return new Promise((resolve) => {
            if (waitForAck) {
                const timeout = setTimeout(resolve, 5000);
                socket.emit(eventName, payload, () => { clearTimeout(timeout); resolve(); });
            } else {
                socket.emit(eventName, payload);
                resolve();
            }
        });
    };

    await emitTracking("album-upload:join", true);

    const heartbeatTimer = sasPayload.sessionId
        ? setInterval(() => { emitTracking("album-upload:heartbeat"); }, SOCKET_HEARTBEAT_INTERVAL_MS)
        : null;

    try {
        let currentIndex = 0;

        const worker = async () => {
            while (currentIndex < files.length) {
                const fileIndex = currentIndex++;
                const currentFile = files[fileIndex];
                const rawFile = getRawFile(currentFile);
                try {
                    const uploaded = await uploadSingleAlbumImage(currentFile, sasPayload, options, {
                        studio,
                        eventId,
                        serviceId,
                        templateId,
                    });
                    success.push(uploaded);
                    uploadedCount++;
                } catch (error) {
                    failed.push({ fileName: rawFile.name, error: getErrorMessage(error) });
                    failedCount++;
                }
            }
        };

        await Promise.all(Array.from({ length: Math.min(FILE_CONCURRENCY, files.length) }, worker));
    } finally {
        if (heartbeatTimer) clearInterval(heartbeatTimer);
        await emitTracking("album-upload:complete", true);
        socket?.disconnect();
    }

    return { success, failed };
};

export const listAlbumCuratedMedia = (data: {
    studioId: string;
    eventId: string;
    serviceId?: string | null;
    templateId: string;
    itemIndex?: number | null;
    limit?: number;
    continuationToken?: string | null;
}) =>
    axiosBase
        .post(ALBUM_LIST_ENDPOINT, data)
        .then((r) => r.data)
        .catch(errorHandler);


export const updateAlbumUploadStatus = (data: {
    studioId: string;
    eventId: string;
    sessionId: string;
    totalFiles?: number;
    uploadedCount?: number;
    failedCount?: number;
    complete?: boolean;
}) =>
    axiosBase
        .post(ALBUM_UPLOAD_STATUS_ENDPOINT, data)
        .then((r) => r.data)
        .catch(errorHandler);

const eventAlbumSelectionUploadService = {
    uploadAlbumImages,
    listAlbumCuratedMedia,
    updateAlbumUploadStatus,
};

export default eventAlbumSelectionUploadService;
